import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import { colors } from '../theme/theme';

import { AmortizationScheduleResponse, MessageBubbleProps } from './IMessageBubbleInterface';
import { AmortizationScheduleCard } from './MessageBubbleCards/AmortizationScheduleCard';
import { ContractDateRangeCard } from './MessageBubbleCards/ContractDateRangeCard';
import { ContractDetailsCard } from './MessageBubbleCards/ContractDetailsCard';
import { ContractFinancialSummaryCard } from './MessageBubbleCards/ContractFinancialSummaryCard';
import { ContractMetricBulletsCard } from './MessageBubbleCards/ContractMetricBulletsCard';
import { ContractMetricListCard } from './MessageBubbleCards/ContractMetricListCard';
import { ContractsCard } from './MessageBubbleCards/ContractsCard';
import { ContractSnapshotCard } from './MessageBubbleCards/ContractSnapshotCard';
import { GenericBotResponseCard } from './MessageBubbleCards/GenericBotResponseCard';
import { InvoicesCard } from './MessageBubbleCards/InvoicesCard';
import { LinkedAssetsCard } from './MessageBubbleCards/LinkedAssetsCard';
import { NextPaymentDueCard } from './MessageBubbleCards/NextPaymentDueCard';
import { PaymentHistoryCard } from './MessageBubbleCards/PaymentHistoryCard';
import { PlainTextResponseCard } from './MessageBubbleCards/PlainTextResponseCard';
import { ScheduledPaymentsCard } from './MessageBubbleCards/ScheduledPaymentsCard';
import { SingleContractMetricCard } from './MessageBubbleCards/SingleContractMetricCard';
import { SupportRequestsCard } from './MessageBubbleCards/SupportRequestsCard';
import {
    extractPayloadText,
    formatTime,
    hasSimpleInstructionSteps,
    isClarificationPrompt,
    parseAmortizationSchedule,
    parseAutoPayStatus,
    parseContractDateRange,
    parseContractDetails,
    parseContractFinancialSummary,
    parseContractMetricBullets,
    parseContractMetricList,
    parseContracts,
    parseContractSnapshotPayload,
    parseGenericSections,
    parseInvoices,
    parseLinkedAssets,
    parseNextPaymentDue,
    parsePaymentHistory,
    parseScheduledPayments,
    parseSingleContractMetric,
    parseSupportRequests,
    shouldForceGuidanceCardUi,
} from './MessageBubbleHelpers';

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onShowQuickActions }) => {
    const isUserMessage = message.type === 'user';
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const brandPrimaryColor = brandDetails?.PrimaryColour || colors.bluePreset[10];

    // When the user explicitly asks about dates (start/end/maturity/etc.), show only the
    // date fields on contract cards. Any other query keeps the full existing card.
    const isDateIntentQuery = (() => {
        const query = (message.query || '').toLowerCase();
        if (!query) {
            return false;
        }
        const mentionsDate =
            /\b(date|dates|start|begin|started|end|ends|ended|maturity|mature|expire|expires|expiry|when)\b/.test(
                query,
            );
        // Guard against amount/count/status queries that merely contain a date-ish word.
        const mentionsNonDateTopic =
            /\b(amount|payment\s+amount|balance|principal|interest|how\s+much|percentage|status|count|how\s+many)\b/.test(
                query,
            );
        // Payment-centric questions ("when is my next payment due?", "next payment") want the full
        // payment card (next due + amount + frequency), not a date-only view that hides everything
        // but the date — and leaves an empty card when next_payment_date is null.
        const mentionsPaymentTopic = /\b(payment|payments|pay|due|installment|emi)\b/.test(query);
        return mentionsDate && !mentionsNonDateTopic && !mentionsPaymentTopic;
    })();

    // When the user's query mentions scheduling (partial match on "schedul" covers
    // schedule/scheduled/scheduling), prefer the scheduled-payments UI for array payloads
    // instead of guessing from payload field names.
    const isScheduledPaymentsQuery = /schedul/i.test(message.query || '');

    // Defensive bound: the bot-payload parsers below run many regexes over this text. Chatbot
    // responses are short, so capping the input length keeps every parser's runtime linear-bounded
    // and removes any super-linear/ReDoS (S5852) exposure on adversarial/oversized payloads, with no
    // change to parsing behavior for real responses.
    const MAX_PARSE_CHARS = 20000;
    const safeMessageText =
        typeof message.text === 'string' && message.text.length > MAX_PARSE_CHARS
            ? message.text.slice(0, MAX_PARSE_CHARS)
            : message.text;

    const botParserText = safeMessageText;

    // User messages need none of this parsing — bundled into one function so the branch lives
    // in its own scope instead of repeating `!isUserMessage ? ... : null` for every field.
    const parseBotPayloads = () => {
        if (isUserMessage) {
            return {
                botText: safeMessageText,
                invoices: null,
                scheduledPayments: null,
                supportRequests: null,
                paymentHistory: null,
                amortizationSchedule: null,
                contractDetails: null,
                contracts: null,
                contractFinancialSummary: null,
                autoPayStatus: null,
                contractMetricList: null,
                contractSnapshot: null,
                nextPaymentDue: null,
                contractDateRange: null,
                contractMetricBullets: null,
                linkedAssets: null,
                singleContractMetric: null,
                genericBotResponse: null,
            };
        }

        const parsedBotText = extractPayloadText(safeMessageText);
        return {
            botText: parsedBotText,
            invoices: parseInvoices(botParserText),
            scheduledPayments: parseScheduledPayments(botParserText, isScheduledPaymentsQuery),
            supportRequests: parseSupportRequests(botParserText),
            paymentHistory: parsePaymentHistory(botParserText),
            amortizationSchedule: parseAmortizationSchedule(botParserText),
            contractDetails: parseContractDetails(botParserText),
            contracts: parseContracts(botParserText),
            contractFinancialSummary: parseContractFinancialSummary(botParserText),
            autoPayStatus: parseAutoPayStatus(botParserText),
            contractMetricList: parseContractMetricList(botParserText),
            contractSnapshot: parseContractSnapshotPayload(safeMessageText),
            nextPaymentDue: parseNextPaymentDue(botParserText),
            contractDateRange: parseContractDateRange(botParserText),
            contractMetricBullets: parseContractMetricBullets(botParserText),
            linkedAssets: parseLinkedAssets(botParserText),
            singleContractMetric: parseSingleContractMetric(botParserText),
            // GenericBotResponseCard's own internal parseGenericSections(...) call would make the
            // component always render (even to `null`), so this call determines nullability up
            // front — preserving the original `renderGenericBotResponse` semantics where
            // `genericBotResponse` is `null` (not a truthy-but-empty element) when there's no
            // rich structure, so the `if (genericBotResponse)` fallback chain in renderBotContent
            // behaves identically to before.
            genericBotResponse: parseGenericSections(parsedBotText) ? (
                <GenericBotResponseCard text={parsedBotText} />
            ) : null,
        };
    };

    const {
        botText,
        invoices,
        scheduledPayments,
        supportRequests,
        paymentHistory,
        amortizationSchedule,
        contractDetails,
        contracts,
        contractFinancialSummary,
        autoPayStatus,
        contractMetricList,
        contractSnapshot,
        nextPaymentDue,
        contractDateRange,
        contractMetricBullets,
        linkedAssets,
        singleContractMetric,
        genericBotResponse,
    } = parseBotPayloads();

    const amortizationScheduleData: AmortizationScheduleResponse = amortizationSchedule || {
        intro: '',
        contractId: '',
        contractName: '',
        summaryLine: '',
        stats: [],
        upcomingPayments: [],
        followUp: null,
    };

    const ENABLE_AMORTIZATION_LIST_VIEW = false;

    // Ordered card resolvers, evaluated in priority order — the first one to return a non-null
    // node wins. Kept as data (rather than a chain of `if...return`) so priority order is easy to
    // scan and adding/reordering a card doesn't add branching complexity to renderBotContent.
    const botCardResolvers: Array<() => React.ReactNode | null> = [
        () => (invoices && invoices.items.length > 0 ? <InvoicesCard invoices={invoices} /> : null),
        () =>
            scheduledPayments ? (
                <ScheduledPaymentsCard
                    scheduledPayments={scheduledPayments}
                    botText={botText}
                    brandPrimaryColor={brandPrimaryColor}
                />
            ) : null,
        // AutoPay status (JSON or text) renders as the clean metric card — must win over the heavy
        // "Contracts Overview" card and the generic metric-list parser.
        () => (autoPayStatus ? <ContractMetricListCard data={autoPayStatus} /> : null),
        () => (nextPaymentDue ? <NextPaymentDueCard nextPaymentDue={nextPaymentDue} /> : null),
        () => (contractDateRange ? <ContractDateRangeCard contractDateRange={contractDateRange} /> : null),
        () => (linkedAssets ? <LinkedAssetsCard linkedAssets={linkedAssets} /> : null),
        () =>
            contractMetricBullets ? <ContractMetricBulletsCard contractMetricBullets={contractMetricBullets} /> : null,
        () => (singleContractMetric ? <SingleContractMetricCard singleContractMetric={singleContractMetric} /> : null),
        () => (contractDetails ? <ContractDetailsCard contractDetails={contractDetails} /> : null),
        () => (contracts ? <ContractsCard contracts={contracts} isDateIntentQuery={isDateIntentQuery} /> : null),
        () =>
            contractFinancialSummary ? (
                <ContractFinancialSummaryCard contractFinancialSummary={contractFinancialSummary} />
            ) : null,
        () => (contractMetricList ? <ContractMetricListCard data={contractMetricList} /> : null),
        () => (contractSnapshot ? <ContractSnapshotCard contractSnapshot={contractSnapshot} /> : null),
        () => (supportRequests ? <SupportRequestsCard supportRequests={supportRequests} /> : null),
        () => (paymentHistory ? <PaymentHistoryCard paymentHistory={paymentHistory} /> : null),
        () =>
            ENABLE_AMORTIZATION_LIST_VIEW && amortizationSchedule ? (
                <AmortizationScheduleCard amortizationScheduleData={amortizationScheduleData} />
            ) : null,
        () => (!isUserMessage && hasSimpleInstructionSteps(botText) ? <PlainTextResponseCard text={botText} /> : null),
        () => (!isUserMessage && shouldForceGuidanceCardUi(botText) ? <PlainTextResponseCard text={botText} /> : null),
        () => genericBotResponse,
    ];

    const renderBotContent = () => {
        // Clarification prompts (e.g. "Please provide the contract ID ... so I can look up the
        // asset serial number for you.") are plain prose that merely mentions domain words like
        // "asset serial number" / "contract" and must NOT be parsed into asset/contract cards —
        // render them as plain text before any card resolver can claim them.
        if (isClarificationPrompt(botText, isUserMessage)) {
            return <PlainTextResponseCard text={botText} />;
        }

        for (const resolveCard of botCardResolvers) {
            const card = resolveCard();
            if (card) return card;
        }
        return <PlainTextResponseCard text={botText} />;
    };

    const renderAvatar = (iconClassName: string) => (
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: brandPrimaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.5,
            }}
        >
            <span
                className={iconClassName}
                style={{
                    fontSize: 16,
                    color: colors.fullWhite,
                }}
            />
        </Box>
    );

    return (
        <Stack alignItems="flex-start" gap={0.5} sx={{ mb: 1.5 }} direction={isUserMessage ? 'row-reverse' : 'row'}>
            {!isUserMessage && renderAvatar('icon-rounded-chat')}
            {isUserMessage && renderAvatar('icon-user')}
            <Stack alignItems={isUserMessage ? 'flex-end' : 'flex-start'} gap={0.3} sx={{ flex: 1 }}>
                <Box
                    sx={{
                        width: isUserMessage ? 'auto' : '84%',
                        maxWidth: '100%',
                        overflowY: 'auto',
                        backgroundColor: isUserMessage ? brandPrimaryColor : colors.fullWhite,
                        border: isUserMessage ? 'none' : `1px solid ${colors.greyPreset[20]}`,
                        borderRadius: '14px',
                        p: 1.5,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: colors.greyPreset[40],
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: colors.greyPreset[50],
                        },
                    }}
                >
                    {isUserMessage ? (
                        <Typography
                            variant="body2"
                            sx={{
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-line',
                                lineHeight: 1.65,
                                color: colors.fullWhite,
                                px: 0.25,
                            }}
                        >
                            {message.text}
                        </Typography>
                    ) : (
                        <Stack gap={1.5}>
                            {renderBotContent()}
                            {onShowQuickActions && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                                    <Box
                                        component="button"
                                        onClick={onShowQuickActions}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            width: '100%',
                                            px: 1.2,
                                            py: 0.95,
                                            lineHeight: 1.2,
                                            border: 'none',
                                            borderRadius: '14px',
                                            backgroundColor: brandPrimaryColor,
                                            color: colors.fullWhite,
                                            cursor: 'pointer',
                                            fontSize: '0.98rem',
                                            fontWeight: 700,
                                            transition: 'all 0.2s ease',
                                            textAlign: 'left',
                                            '&:hover': {
                                                opacity: 0.94,
                                                boxShadow: '0 3px 12px rgba(10, 20, 80, 0.2)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255, 255, 255, 0.16)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <span
                                                className="icon-tip"
                                                style={{ fontSize: 14, color: colors.fullWhite }}
                                            />
                                        </Box>
                                        Show quick actions
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    )}
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: colors.greyPreset[50],
                        px: 1,
                        fontSize: '12px',
                    }}
                >
                    {formatTime(message.timestamp)}
                </Typography>
            </Stack>
        </Stack>
    );
};
